import _ from 'lodash';
import validateCreditCards from './validateCreditCards';
import validatePassengers from './validatePassengers';
import validateUserRemarks from './validateUserRemarks';
import validateCheckoutFields from './validateAdditionalCheckoutFields';
import validateHotelSpecialRequests from './validateHotelSpecialRequests';
import validateTourTravelers, { validateTourBookingQuestions } from './validateTourTravelers';
import validateTourPickup from './validateTourPickup';
import validateTourProviderAccount from './validateTourProviderAccount';
import buildValidationErrorSummary from './buildValidationErrorSummary';
import validateInsurance from './validateInsurance';
import { InsuranceMode } from '../../constants';

const validateCheckoutForms = (
  trip,
  passengers,
  paymentMethods,
  states,
  isBooking,
  travelAgency,
  tripConfirmation,
  doIgnoreEmptyPassengerDetailValidation,
  doChargeAgencyPlanningFees,
  agencyPlanningFees,
  hasReviewedBookingTripPolicy,
  isRequiredBookedTripPolicyReview,
  hasReviewedBookedTripPolicy,
  isLoggedIn,
  doHoldOnlyCruiseAsOption,
  itinerariesToBook,
  doSplitServiceFees,
  agencyPlanningFeesKeptByAgency,
  acceptedPaymentCards,
  isBookingReviewManual,
  noteAirDeskRequest,
  hasAgreedForDataCollection,
  selectedAdditionalCheckoutFields,
  userRemarks,
  chargeableItemOptions,
  tokenizedData,
  loggedInUserEmail,
  hotelChainAdvisorBySegment,
  specialBookingRequests,
  insuranceInfo,
  insuranceTripInfo,
  tourTravelers,
  tourTravelerQuestions,
  tourHotelPickup,
  tourProviderAccount,
  toursToBook,
) => {
  const isBookedTripPolicyReviewValidated =
    !isRequiredBookedTripPolicyReview || hasReviewedBookedTripPolicy;
  const isBookingTripPolicyReviewValidated = hasReviewedBookingTripPolicy;
  const isNoteAirDeskValidated =
    !isBookingReviewManual ||
    (noteAirDeskRequest && noteAirDeskRequest.length > 0 && noteAirDeskRequest.trim().length > 0);
  const isUserRemarksValidated = validateUserRemarks(userRemarks);

  const invalidHotelSpecialRequestsBySegmentId = validateHotelSpecialRequests(
    trip,
    specialBookingRequests,
    hotelChainAdvisorBySegment,
  );
  const isHotelSpecialRequestsValid = _.isEmpty(invalidHotelSpecialRequestsBySegmentId);

  const invalidTourTravelersBySegmentId = validateTourTravelers(toursToBook, tourTravelers);
  const isTourTravelersValid = _.isEmpty(invalidTourTravelersBySegmentId);

  const invalidTourQuestionsBySegmentId = validateTourBookingQuestions(
    toursToBook,
    tourTravelerQuestions,
  );
  const isTourQuestionsValid = _.isEmpty(invalidTourQuestionsBySegmentId);

  const invalidTourPickupBySegmentId = validateTourPickup(toursToBook, tourHotelPickup);
  const isTourPickupValid = _.isEmpty(invalidTourPickupBySegmentId);

  const tourProviderAccountError = validateTourProviderAccount(
    toursToBook,
    travelAgency,
    loggedInUserEmail,
    tourProviderAccount,
  );

  const doValidateInsurance =
    !!insuranceInfo || trip.insuranceItinerary?.insuranceMode === InsuranceMode.Standalone;
  const [
    isPassengersValid,
    newPassengersFormsValidation,
    similarPassengersValidation,
    emptyPassengerDetailValidation,
  ] = validatePassengers(
    passengers,
    doHoldOnlyCruiseAsOption,
    travelAgency,
    tripConfirmation,
    doIgnoreEmptyPassengerDetailValidation,
    isLoggedIn,
    loggedInUserEmail,
    itinerariesToBook,
    trip,
    doValidateInsurance,
  );
  const [isCreditCardValid, creditCardsFormValidations, isSavedCardValidated] = validateCreditCards(
    trip.isCardAddressRequired,
    trip.isCardSecurityCodeRequired,
    paymentMethods,
    states,
    isBooking,
    acceptedPaymentCards,
    chargeableItemOptions,
    tokenizedData,
  );

  const [isValidInsurance, insuranceTripInfoFormValidations, insuranceOptionsFormValidations] =
    validateInsurance(trip, insuranceInfo, insuranceTripInfo);

  let isAgencyPlanningFeesValid = doChargeAgencyPlanningFees ? agencyPlanningFees > 0 : true;
  if (
    doSplitServiceFees &&
    isAgencyPlanningFeesValid &&
    (agencyPlanningFeesKeptByAgency > agencyPlanningFees || agencyPlanningFeesKeptByAgency <= 0)
  ) {
    isAgencyPlanningFeesValid = false;
  }
  const invalidAdditionalCheckoutFields = validateCheckoutFields(
    travelAgency.checkoutFields,
    selectedAdditionalCheckoutFields,
    isLoggedIn,
  );
  const isAdditionalCheckoutFieldsValid = _.isEmpty(invalidAdditionalCheckoutFields || {});

  if (
    !isPassengersValid ||
    !isCreditCardValid ||
    !isAgencyPlanningFeesValid ||
    !isAdditionalCheckoutFieldsValid ||
    !_.isEmpty(emptyPassengerDetailValidation || {}) ||
    !isBookedTripPolicyReviewValidated ||
    !isNoteAirDeskValidated ||
    !hasAgreedForDataCollection ||
    !isBookingTripPolicyReviewValidated ||
    !isUserRemarksValidated ||
    !isHotelSpecialRequestsValid ||
    !isTourTravelersValid ||
    !isTourQuestionsValid ||
    !isTourPickupValid ||
    !!tourProviderAccountError ||
    !isValidInsurance
  ) {
    const validationErrorSummary = buildValidationErrorSummary({
      newPassengersFormsValidation,
      creditCardsFormValidations,
      similarPassengersValidation,
      emptyPassengerDetailValidation,
      isBookedTripPolicyReviewValidated,
      isBookingTripPolicyReviewValidated,
      isSavedCardValidated,
      isAgencyPlanningFeesValid,
      invalidAdditionalCheckoutFields,
      isNoteAirDeskValidated,
      isAgreedForDataCollection: hasAgreedForDataCollection,
      isUserRemarksValidated,
      isCreditCardFormValid: isCreditCardValid,
    });

    return [
      false,
      newPassengersFormsValidation,
      creditCardsFormValidations,
      similarPassengersValidation,
      emptyPassengerDetailValidation,
      isBookedTripPolicyReviewValidated,
      isBookingTripPolicyReviewValidated,
      isSavedCardValidated,
      isAgencyPlanningFeesValid,
      invalidAdditionalCheckoutFields,
      isNoteAirDeskValidated,
      hasAgreedForDataCollection,
      isUserRemarksValidated,
      invalidHotelSpecialRequestsBySegmentId,
      invalidTourTravelersBySegmentId,
      invalidTourQuestionsBySegmentId,
      invalidTourPickupBySegmentId,
      insuranceTripInfoFormValidations,
      insuranceOptionsFormValidations,
      validationErrorSummary,
      tourProviderAccountError,
    ];
  }
  return [true];
};
export default validateCheckoutForms;
